import { useState, useEffect, useCallback } from "react";
import {
  Table,
  Download,
  RefreshCw,
  Loader2,
  AlertCircle,
} from "lucide-react";
import type { RegistrationRecord } from "@invitation-system/shared";
import { listRegistrations } from "../api/client";

export function RegistrationViewer() {
  const [records, setRecords] = useState<RegistrationRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>("");

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError("");
    try {
      const response = await listRegistrations();
      setRecords(response.registrations);
    } catch {
      setError("加载报名数据失败，请稍后重试。");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = useCallback(() => {
    exportToExcel(records);
  }, [records]);

  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Table className="h-6 w-6 text-primary" aria-hidden="true" />
            报名数据
          </h2>
          <p className="text-muted text-sm mt-1">
            {!isLoading && `共 ${records.length} 条记录`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={fetchData}
            disabled={isLoading}
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-border bg-surface px-4 py-2 text-sm font-medium text-foreground shadow-sm transition-colors duration-200 hover:bg-background hover:border-primary-light disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="刷新数据"
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
              aria-hidden="true"
            />
            刷新
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={isLoading || records.length === 0}
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors duration-200 hover:bg-accent-dark disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            导出 Excel
          </button>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-16 bg-surface rounded-xl shadow-sm">
          <Loader2 className="h-8 w-8 text-primary animate-spin" aria-hidden="true" />
          <span className="ml-3 text-muted" role="status" aria-live="polite">
            正在加载数据…
          </span>
        </div>
      )}

      {/* Error state */}
      {error && !isLoading && (
        <div
          className="flex items-center gap-3 rounded-xl bg-error-light p-4"
          role="alert"
        >
          <AlertCircle className="h-5 w-5 text-error shrink-0" aria-hidden="true" />
          <p className="text-sm text-error font-medium flex-1">{error}</p>
          <button
            type="button"
            onClick={fetchData}
            className="cursor-pointer text-sm font-medium text-error underline hover:no-underline"
          >
            重试
          </button>
        </div>
      )}

      {/* Data table */}
      {!isLoading && !error && (
        <div className="overflow-x-auto rounded-xl border border-border shadow-sm bg-surface">
          <table className="w-full text-sm">
            <thead className="bg-background border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-muted whitespace-nowrap">姓名</th>
                <th className="px-4 py-3 text-left font-medium text-muted whitespace-nowrap">手机</th>
                <th className="px-4 py-3 text-left font-medium text-muted whitespace-nowrap">邮箱</th>
                <th className="px-4 py-3 text-left font-medium text-muted whitespace-nowrap">公司</th>
                <th className="px-4 py-3 text-left font-medium text-muted whitespace-nowrap">职位</th>
                <th className="px-4 py-3 text-left font-medium text-muted whitespace-nowrap">城市</th>
                <th className="px-4 py-3 text-left font-medium text-muted whitespace-nowrap">邀请人</th>
                <th className="px-4 py-3 text-left font-medium text-muted whitespace-nowrap">提交时间</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {records.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-muted">
                    暂无报名记录
                  </td>
                </tr>
              ) : (
                records.map((record) => (
                  <tr
                    key={record.registrationId}
                    className="hover:bg-background/50 transition-colors"
                  >
                    <td className="px-4 py-3 text-foreground font-medium whitespace-nowrap">{record.name}</td>
                    <td className="px-4 py-3 text-foreground whitespace-nowrap">{record.phone}</td>
                    <td className="px-4 py-3 text-foreground">{record.email}</td>
                    <td className="px-4 py-3 text-foreground">{record.company}</td>
                    <td className="px-4 py-3 text-foreground">{record.position}</td>
                    <td className="px-4 py-3 text-foreground whitespace-nowrap">{record.city}</td>
                    <td className="px-4 py-3 text-foreground whitespace-nowrap">{record.inviterDisplayName}</td>
                    <td className="px-4 py-3 text-muted whitespace-nowrap">{formatDate(record.submittedAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function exportToExcel(records: RegistrationRecord[]): void {
  const headers = ["姓名", "手机", "邮箱", "公司", "职位", "城市", "邀请人", "提交时间"];

  const rows = records.map((r) => [
    r.name, r.phone, r.email, r.company, r.position, r.city,
    r.inviterDisplayName, r.submittedAt,
  ]);

  const csvContent = [headers, ...rows]
    .map((row) =>
      row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
    )
    .join("\r\n");

  const bom = "\uFEFF";
  const blob = new Blob([bom + csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `报名数据_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
